import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./ReporteDetalleModal.css";
import Loading from "@shared/ui/loading/Loading";
import { FullPageLoader } from "@/components/shared/LoadingSystem";
import { generatePDF } from "../../../../services/pdfServiceNew";
import { sileo } from "sileo";
import reportesEscaneadosService from "@shared/api/reportesEscaneadosService";
import reportesService from "@shared/api/reportesService";
import planAnualService from "@shared/api/planAnualService";
import imageCacheService from "@shared/lib/imageCache";
import ScannedPdfViewer from "@shared/ui/ScannedPdfViewer";

// Componentes de secciones
import Section from "./Section";
import ChecklistSection from "./ChecklistSection";
import InformacionGeneral from "./InformacionGeneral";
import TrabajoARealizar from "./TrabajoARealizar";
import MatrizCausaEfecto from "./MatrizCausaEfecto";
import Observaciones from "./Observaciones";
import ConclusionesRecomendaciones from "./ConclusionesRecomendaciones";
import Importante from "./Importante";
import FirmasConformidad from "./FirmasConformidad";
import Comentarios from "./Comentarios";
import ImagenesEvidencia from "./ImagenesEvidencia";
import ActividadesRealizadas from "./ActividadesRealizadas";
import { getReportPlantId, getReportTipoId } from "../../lib/reportFields";

/**
 * Componente reutilizable para mostrar el detalle de un reporte de mantenimiento.
 *
 * Props:
 * - report: objeto del reporte a mostrar (con respuesta, secciones, etc.)
 * - isOpen: booleano que controla la visibilidad del modal
 * - onClose: callback al cerrar el modal
 * - onVinculado: callback cuando se vincula exitosamente (opcional)
 * - userRole: rol del usuario actual (opcional, para mostrar/ocultar opciones)
 */
const ReporteDetalleModal = ({
  report,
  isOpen,
  onClose,
  onVinculado,
  userRole,
}) => {
  const [modalState, setModalState] = useState({
    loading: false,
    mode: "digital",
    scannedUrl: null,
    showPanel: false,
    actividades: [],
    actividadesADesvincular: [],
    search: "",
  });
  const [actividadesVinculables, setActividadesVinculables] = useState([]);
  const [actionState, setActionState] = useState({
    generatingPDF: false,
    loadingActs: false,
    vinculando: false,
    desvinculando: false,
    desvinculandoEscaneado: false,
    addedVinculaciones: 0,
    removedVinculaciones: 0,
  });
  const [scanLinked, setScanLinked] = useState(
    Boolean(report?.isScann && report?.scanPath),
  );
  const [auditState, setAuditState] = useState({
    loading: false,
    historial: [],
  });

  const canViewAuditHistory = Number(userRole) !== 3;

  // Determinar si es híbrido
  const tieneDigital =
    report?.respuesta?.secciones && report.respuesta.secciones.length > 0;
  const tieneEscaneado = scanLinked && report?.scanPath;
  const isHybrid = tieneDigital && tieneEscaneado;
  const canUnlinkScanned =
    isHybrid &&
    modalState.mode === "escaneado" &&
    [1, 2, 7].includes(Number(userRole));

  // Contar vinculaciones del reporte (derivado de props + delta de sesión)
  const reporteVinculaciones = useMemo(() => {
    if (!report) return 0;
    const base =
      report.actividadesProgramadas?.length ||
      (report.actividadProgramada ? 1 : 0);
    return (
      base +
      actionState.addedVinculaciones -
      actionState.removedVinculaciones
    );
  }, [report, actionState.addedVinculaciones, actionState.removedVinculaciones]);

  const historialOrdenado = useMemo(
    () =>
      [...(Array.isArray(auditState.historial) ? auditState.historial : [])].sort(
        (a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0),
      ),
    [auditState.historial],
  );

  // Cargar actividades vinculables (PENDIENTE, EN_PROGRESO, COMPLETADA)
  const cargarActividadesVinculables = useCallback(async () => {
    if (!report) return;

    const idPlanta = getReportPlantId(report);
    if (!idPlanta) {
      sileo.warning({ title: "No se puede determinar la planta del reporte" });
      return;
    }

    setActionState((p) => ({ ...p, loadingActs: true }));
    try {
      const idTipoMantenimiento = getReportTipoId(report);
      const actividades = await planAnualService.getActividadesVinculables(
        idPlanta,
        idTipoMantenimiento,
      );
      setActividadesVinculables(actividades || []);
    } catch (error) {
      console.error("Error cargando actividades vinculables:", error);
      setActividadesVinculables([]);
    } finally {
      setActionState((p) => ({ ...p, loadingActs: false }));
    }
  }, [report]);

  // Vincular reporte con las actividades seleccionadas (multi)
  const handleVincular = useCallback(async () => {
    if (!modalState.actividades.length || !report) return;

    const idReporte = report.idreporte;
    setActionState((p) => ({ ...p, vinculando: true }));
    try {
      await Promise.all(
        modalState.actividades.map((act) =>
          planAnualService.vincularConReporte(
            act.idactividadprogramada,
            idReporte,
          ),
        ),
      );
      const count = modalState.actividades.length;
      sileo.success({
        title:
          count === 1
            ? "Reporte vinculado exitosamente a la actividad"
            : `Reporte vinculado a ${count} actividades`,
      });
      setModalState((p) => ({
        ...p,
        showPanel: false,
        actividades: [],
        actividadesADesvincular: [],
        search: "",
      }));
      setActionState((p) => ({
        ...p,
        addedVinculaciones: p.addedVinculaciones + count,
      }));
      if (onVinculado) {
        onVinculado(report, modalState.actividades);
      }
    } catch (error) {
      console.error("Error al vincular:", error);
      sileo.error({
        title:
          error.response?.data?.error ||
          "Error al vincular el reporte con las actividades",
      });
    } finally {
      setActionState((p) => ({ ...p, vinculando: false }));
    }
  }, [modalState.actividades, report, onVinculado]);

  // Toggle de selección de una actividad en la lista multi-select
  const toggleActividadSeleccion = useCallback((actividad) => {
    setModalState((p) => {
      const id = actividad.idactividadprogramada;
      const ya = p.actividades.some((a) => a.idactividadprogramada === id);
      return {
        ...p,
        actividades: ya
          ? p.actividades.filter((a) => a.idactividadprogramada !== id)
          : [...p.actividades, actividad],
      };
    });
  }, []);

  // Abrir panel de vinculación
  const handleOpenVincular = useCallback(() => {
    setModalState((p) => ({
      ...p,
      showPanel: true,
      actividades: [],
      actividadesADesvincular: [],
      search: "",
    }));
    cargarActividadesVinculables();
  }, [cargarActividadesVinculables]);

  // Toggle de selección de actividades ya vinculadas para desvincular
  const toggleActividadDesvincular = useCallback((actividad) => {
    setModalState((p) => {
      const id = actividad.idactividadprogramada;
      const ya = p.actividadesADesvincular.some(
        (a) => a.idactividadprogramada === id,
      );
      return {
        ...p,
        actividadesADesvincular: ya
          ? p.actividadesADesvincular.filter(
              (a) => a.idactividadprogramada !== id,
            )
          : [...p.actividadesADesvincular, actividad],
      };
    });
  }, []);

  // Desvincular reporte de las actividades seleccionadas (multi)
  const handleDesvincular = useCallback(async () => {
    if (!modalState.actividadesADesvincular.length || !report) return;

    const idReporte = report.idreporte;
    setActionState((p) => ({ ...p, desvinculando: true }));
    try {
      await Promise.all(
        modalState.actividadesADesvincular.map((act) =>
          planAnualService.desvincularReporte(
            act.idactividadprogramada,
            idReporte,
          ),
        ),
      );
      const count = modalState.actividadesADesvincular.length;
      sileo.success({
        title:
          count === 1
            ? "Reporte desvinculado de la actividad"
            : `Reporte desvinculado de ${count} actividades`,
      });
      setActionState((p) => ({
        ...p,
        removedVinculaciones: p.removedVinculaciones + count,
      }));
      setModalState((p) => ({ ...p, actividadesADesvincular: [] }));
      await cargarActividadesVinculables();
      if (onVinculado) {
        onVinculado(report, modalState.actividadesADesvincular);
      }
    } catch (error) {
      console.error("Error al desvincular:", error);
      sileo.error({
        title:
          error.response?.data?.error ||
          "Error al desvincular el reporte de las actividades",
      });
    } finally {
      setActionState((p) => ({ ...p, desvinculando: false }));
    }
  }, [modalState.actividadesADesvincular, report, cargarActividadesVinculables, onVinculado]);

  // Filtrar actividades por búsqueda
  const actividadesFiltradas = actividadesVinculables.filter((act) => {
    if (!modalState.search.trim()) return true;
    const query = modalState.search.toLowerCase();
    const titulo = (act.plantilla?.titulo || act.titulo || "").toLowerCase();
    const tipo = (act.tipoMantenimiento?.tipomantenimiento || "").toLowerCase();
    const subcomps = (act.subcomponentes || [])
      .map((s) => s.descripcion?.toLowerCase() || "")
      .join(" ");
    return (
      titulo.includes(query) || tipo.includes(query) || subcomps.includes(query)
    );
  });

  // Verificar si el reporte actual ya está vinculado a una actividad
  const isAlreadyLinkedTo = useCallback(
    (actividad) => {
      if (!report) return false;
      return (actividad.reportes || []).some(
        (r) => r.idreporte === report.idreporte,
      );
    },
    [report],
  );

  // Formatear fecha de actividad
  const formatFechaActividad = (dateString) => {
    if (!dateString) return "Sin fecha";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAuditDate = (dateString) => {
    if (!dateString) return "Fecha no disponible";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Fecha no disponible";
    return date.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAuditActionLabel = (action) => {
    const labels = {
      crear: "Creación del reporte",
      actualizar: "Actualización del reporte",
      agregar_escaneado: "Se agregó PDF escaneado",
      agregar_digital: "Se agregó contenido digital",
      desvincular_escaneado: "Se desvinculó PDF escaneado",
    };
    return labels[action] || action || "Acción desconocida";
  };

  const getAuditDetailText = (entry) => {
    if (!entry) return "";

    if (typeof entry.detalle === "string" && entry.detalle.trim()) {
      return entry.detalle.trim();
    }

    if (Array.isArray(entry.cambios) && entry.cambios.length > 0) {
      return entry.cambios.join(". ");
    }

    return "";
  };

  // Ordenar secciones y configurar vista cuando se abre el modal
  useEffect(() => {
    if (!isOpen || !report) return;

    setScanLinked(Boolean(report.isScann && report.scanPath));

    // Reset vincular panel al abrir
    setModalState((p) => ({
      ...p,
      showPanel: false,
      actividades: [],
      actividadesADesvincular: [],
      search: "",
    }));

    // Ordenar secciones por campo 'orden'
    if (report.respuesta?.secciones) {
      report.respuesta.secciones.sort((a, b) => {
        const ordenA = a?.orden || 999;
        const ordenB = b?.orden || 999;
        return ordenA - ordenB;
      });
    }

    // Determinar vista por defecto
    const hasDigital =
      report.respuesta?.secciones && report.respuesta.secciones.length > 0;
    const hasScanned = report.isScann && report.scanPath;
    const defaultMode = hasScanned && !hasDigital ? "escaneado" : "digital";

    setModalState({
      loading: true,
      scannedUrl: null,
      mode: defaultMode,
      showPanel: false,
      actividades: [],
      actividadesADesvincular: [],
      search: "",
    });

    // Cargar URL del PDF escaneado
    if (hasScanned) {
      const loadScannedUrl = async () => {
        try {
          const urlData = report.scanPath
            ? await reportesEscaneadosService.obtenerUrlVisualizacion(
                report.scanPath,
              )
            : await reportesEscaneadosService.obtenerUrlVisualizacion(
                report.idreporte,
              );

          if (urlData.success && urlData.data?.url) {
            setModalState((p) => ({
              ...p,
              scannedUrl: urlData.data.url + "#t=" + Date.now(),
            }));
          }
        } catch (error) {
          console.error("Error al obtener URL del PDF:", error);
        }
      };
      loadScannedUrl();
    }

    if (canViewAuditHistory) {
      setAuditState({ loading: true, historial: [] });
      const loadAuditHistory = async () => {
        try {
          const data = await reportesService.obtenerHistorialAuditoria(
            report.idreporte,
          );
          setAuditState({
            loading: false,
            historial: Array.isArray(data?.historial) ? data.historial : [],
          });
        } catch (error) {
          console.error("Error cargando historial de auditoría:", error);
          setAuditState({ loading: false, historial: [] });
        }
      };

      loadAuditHistory();
    } else {
      setAuditState({ loading: false, historial: [] });
    }

    const timer = setTimeout(() => {
      setModalState((p) => ({ ...p, loading: false }));
    }, 1000);

    return () => clearTimeout(timer);
  }, [isOpen, report, canViewAuditHistory]);

  // Reintentar carga de URL de PDF escaneado (limpia caché y re-solicita)
  const handleRetryScannedUrl = useCallback(async () => {
    if (!report?.scanPath) return;
    // Limpiar caché para forzar nueva URL desde el backend
    imageCacheService.removeFromCache(report.scanPath);
    setModalState((p) => ({ ...p, scannedUrl: null }));
    try {
      const urlData = await reportesEscaneadosService.obtenerUrlVisualizacion(
        report.scanPath,
      );
      if (urlData.success && urlData.data?.url) {
        // Fragment cache-buster para forzar al iframe a hacer nueva petición
        setModalState((p) => ({
          ...p,
          scannedUrl: urlData.data.url + "#t=" + Date.now(),
        }));
      } else {
        sileo.error({ title: "No se pudo obtener la URL del PDF" });
      }
    } catch (error) {
      console.error("Error al reintentar URL del PDF:", error);
      sileo.error({ title: "Error al recargar el PDF" });
    }
  }, [report]);

  // Descargar PDF
  const handlePrintReport = useCallback(async () => {
    if (!report?.respuesta) return;

    setActionState((p) => ({ ...p, generatingPDF: true }));
    try {
      await generatePDF(report.respuesta, { idTipoMantenimiento: report.idtipomantenimiento });
      sileo.success({ title: "PDF generado correctamente" });
    } catch (error) {
      console.error("Error generando PDF:", error);
      sileo.error({ title: "Error al generar el PDF" });
    } finally {
      setActionState((p) => ({ ...p, generatingPDF: false }));
    }
  }, [report]);

  const handleDesvincularEscaneado = useCallback(async () => {
    if (!report?.idreporte) return;

    if (
      !window.confirm(
        "¿Deseas desvincular el reporte escaneado y conservar únicamente la versión digital?",
      )
    ) {
      return;
    }

    try {
      setActionState((p) => ({ ...p, desvinculandoEscaneado: true }));
      await reportesService.desvincularEscaneado(report.idreporte);
      sileo.success({
        title: "Escaneado desvinculado correctamente",
      });

      // Actualización local inmediata para mantener el modal abierto y consistente
      setScanLinked(false);
      setModalState((p) => ({
        ...p,
        mode: "digital",
        scannedUrl: null,
      }));

      if (onVinculado) {
        await Promise.resolve(onVinculado());
      }
    } catch (error) {
      console.error("Error al desvincular escaneado:", error);
      sileo.error({
        title:
          error.response?.data?.error ||
          "Error al desvincular el escaneado del reporte",
      });
    } finally {
      setActionState((p) => ({ ...p, desvinculandoEscaneado: false }));
    }
  }, [report, onVinculado, onClose]);

  // Renderizar sección del reporte
  const renderSection = useCallback(
    (seccion) => {
      if (!seccion || !seccion.nombreseccion) return null;

      switch (seccion.nombreseccion) {
        case "Información General":
          return (
            <InformacionGeneral
              key={seccion.idseccionreporte}
              seccion={seccion}
              reportInfo={report.respuesta}
              renderCase="view"
            />
          );
        case "Importante":
          return (
            <Importante
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
        case "Trabajo a Realizar":
          return (
            <TrabajoARealizar
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
        case "Checklist":
          return (
            <ChecklistSection
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
              reportInfo={report.respuesta}
            />
          );
        case "Actividades Realizadas":
          return (
            <ActividadesRealizadas
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
        case "Matriz Causa y Efecto":
        case "Matriz de Causa y Efecto":
          return (
            <MatrizCausaEfecto
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
        case "Imágenes de evidencia":
        case "Imágenes de Evidencia":
          return (
            <ImagenesEvidencia
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
        case "Observaciones":
          return (
            <Observaciones
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
        case "Comentarios":
          return (
            <Comentarios
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
        case "Conclusiones/Recomendaciones":
        case "Conclusiones y Recomendaciones":
          return (
            <ConclusionesRecomendaciones
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
        case "Firmas de conformidad":
        case "Firmas de Conformidad":
          return (
            <FirmasConformidad
              key={seccion.idseccionreporte}
              seccion={seccion}
              reportInfo={report.respuesta}
              renderCase="view"
            />
          );
        default:
          if (seccion.tipoinput === "checklist") {
            return (
              <ChecklistSection
                key={seccion.idseccionreporte}
                seccion={seccion}
                renderCase="view"
                reportInfo={report.respuesta}
              />
            );
          }
          return (
            <Section
              key={seccion.idseccionreporte}
              seccion={seccion}
              renderCase="view"
            />
          );
      }
    },
    [report],
  );

  if (!isOpen || !report) return null;

  return (
    <>
      {/* Modal de detalle del reporte */}
      <div
        role="button"
        tabIndex={0}
        className="report-modal-overlay"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClose(e);
          }
        }}
      >
        <div
          role="button"
          tabIndex={0}
          className="report-modal-content"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              ((e) => e.stopPropagation())(e);
            }
          }}
        >
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>

          {modalState.loading ? (
            <div className="modal-loading">
              <Loading type="report" />
            </div>
          ) : (
            <>
              <div className="modal-header">
                <h2 className="modal-title">
                  <i
                    className={`fa-solid ${
                      isHybrid
                        ? "fa-layer-group"
                        : report.isScann
                          ? "fa-file-pdf"
                          : "fa-file-alt"
                    }`}
                  ></i>
                  {isHybrid
                    ? "Reporte Híbrido"
                    : report.isScann
                      ? "Reporte Escaneado"
                      : "Detalle del Reporte"}
                </h2>

                {/* Switch para reportes híbridos */}
                {isHybrid && (
                  <div className="hybrid-view-switch">
                    <button
                      className={`hybrid-switch-btn ${modalState.mode === "digital" ? "active" : ""}`}
                      onClick={() =>
                        setModalState((p) => ({ ...p, mode: "digital" }))
                      }
                    >
                      <i className="fa-solid fa-file-alt"></i>
                      Digital
                    </button>
                    <button
                      className={`hybrid-switch-btn ${modalState.mode === "escaneado" ? "active" : ""}`}
                      onClick={() =>
                        setModalState((p) => ({ ...p, mode: "escaneado" }))
                      }
                    >
                      <i className="fa-solid fa-file-pdf"></i>
                      Escaneado
                    </button>
                  </div>
                )}

                <div className="modal-header-actions">
                  {canUnlinkScanned && (
                    <button
                      className="desvincular-escaneado-btn"
                      onClick={handleDesvincularEscaneado}
                      disabled={actionState.desvinculandoEscaneado}
                      title="Desvincular reporte escaneado"
                    >
                      {actionState.desvinculandoEscaneado ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin"></i>
                          Desvinculando...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-link-slash"></i>
                          Desvincular Escaneado
                        </>
                      )}
                    </button>
                  )}

                  {/* Botón vincular a actividad (disponible para todos excepto Supervisor rol 3) */}
                  {userRole !== 3 && (
                    <button
                      className={`vincular-actividad-btn ${modalState.showPanel ? "active" : ""}`}
                      onClick={() =>
                        modalState.showPanel
                          ? setModalState((p) => ({ ...p, showPanel: false }))
                          : handleOpenVincular()
                      }
                      title="Vincular a una actividad programada"
                    >
                      <i
                        className={`fa-solid ${modalState.showPanel ? "fa-times" : "fa-link"}`}
                      ></i>
                      <span>
                        {modalState.showPanel
                          ? "Cancelar"
                          : reporteVinculaciones > 0
                            ? `Vinculado (${reporteVinculaciones})`
                            : "Vincular"}
                      </span>
                    </button>
                  )}

                  {/* Botón de descargar PDF solo para reportes con datos digitales */}
                  {tieneDigital && (
                    <button
                      className="download-pdf-btn"
                      onClick={handlePrintReport}
                      disabled={actionState.generatingPDF}
                    >
                      {actionState.generatingPDF ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin"></i>
                          Generando...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-download"></i>
                          Descargar PDF
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="modal-body">
                {canViewAuditHistory && (
                  <div className="audit-history-panel">
                    <div className="audit-history-header">
                      <h3>
                        <i className="fa-solid fa-clock-rotate-left"></i>
                        Historial de Auditoría
                      </h3>
                    </div>

                    {auditState.loading ? (
                      <div className="audit-history-loading">
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        <span>Cargando historial...</span>
                      </div>
                    ) : historialOrdenado.length === 0 ? (
                      <div className="audit-history-empty">
                        <i className="fa-solid fa-inbox"></i>
                        <span>Este reporte aún no tiene historial registrado</span>
                      </div>
                    ) : (
                      <div className="audit-history-list">
                        {historialOrdenado.map((entry, index) => (
                          <div
                            className="audit-history-item"
                            key={`${entry.fecha || "sin-fecha"}-${index}`}
                          >
                            <div className="audit-history-item-main">
                              <span className="audit-action">
                                {getAuditActionLabel(entry.accion)}
                              </span>
                              <span className="audit-date">
                                {formatAuditDate(entry.fecha)}
                              </span>
                            </div>
                            {getAuditDetailText(entry) && (
                              <span className="audit-detail">
                                {getAuditDetailText(entry)}
                              </span>
                            )}
                            <span className="audit-user">
                              <i className="fa-solid fa-user"></i>
                              {entry.nombreusuario || "Usuario no identificado"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Panel de vinculación con actividad */}
                {modalState.showPanel && (
                  <div className="vincular-panel">
                    <div className="vincular-panel-header">
                      <h3>
                        <i className="fa-solid fa-link"></i>
                        Vincular a Actividad Programada
                      </h3>
                      <p>
                        Selecciona una actividad para vincularla con este
                        reporte. Puedes vincular múltiples reportes a una misma
                        actividad.
                      </p>
                    </div>

                    <div className="vincular-search">
                      <i className="fa-solid fa-search"></i>
                      <input
                        type="text"
                        placeholder="Buscar actividad por nombre, tipo o subcomponente..."
                        value={modalState.search}
                        onChange={(e) =>
                          setModalState((p) => ({
                            ...p,
                            search: e.target.value,
                          }))
                        }
                      />
                      {modalState.search && (
                        <i
                          role="button"
                          tabIndex={0}
                          className="fa-solid fa-times vincular-search-clear"
                          onClick={() =>
                            setModalState((p) => ({ ...p, search: "" }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              (() =>
                                setModalState((p) => ({ ...p, search: "" })))(
                                e,
                              );
                            }
                          }}
                        ></i>
                      )}
                    </div>

                    <div className="vincular-actividades-list">
                      {actionState.loadingActs ? (
                        <div className="vincular-loading">
                          <i className="fa-solid fa-spinner fa-spin"></i>
                          <p>Cargando actividades pendientes...</p>
                        </div>
                      ) : actividadesFiltradas.length === 0 ? (
                        <div className="vincular-empty">
                          <i className="fa-solid fa-calendar-xmark"></i>
                          <p>
                            {modalState.search
                              ? "No se encontraron actividades con esa búsqueda"
                              : "No hay actividades pendientes para esta planta"}
                          </p>
                        </div>
                      ) : (
                        actividadesFiltradas.map((actividad) => {
                          const alreadyLinked = isAlreadyLinkedTo(actividad);
                          const isSelected = modalState.actividades.some(
                            (a) =>
                              a.idactividadprogramada ===
                              actividad.idactividadprogramada,
                          );
                          return (
                          <div
                            role="button"
                            tabIndex={0}
                            key={actividad.idactividadprogramada}
                            className={`vincular-actividad-item ${
                              isSelected ? "selected" : ""
                            } ${alreadyLinked ? "already-linked" : ""}`}
                            onClick={() =>
                              !alreadyLinked &&
                              toggleActividadSeleccion(actividad)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                e.currentTarget.click();
                            }}
                          >
                            <div className="vincular-actividad-radio">
                              {alreadyLinked ? (
                                <div
                                  className={`check-box check-box-desvincular ${
                                    modalState.actividadesADesvincular.some(
                                      (a) =>
                                        a.idactividadprogramada ===
                                        actividad.idactividadprogramada,
                                    )
                                      ? "checked"
                                      : ""
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleActividadDesvincular(actividad);
                                  }}
                                  title="Seleccionar para desvincular"
                                >
                                  {modalState.actividadesADesvincular.some(
                                    (a) =>
                                      a.idactividadprogramada ===
                                      actividad.idactividadprogramada,
                                  ) && (
                                    <i
                                      className="fa-solid fa-check"
                                      style={{ fontSize: "0.6rem", color: "#fff" }}
                                    ></i>
                                  )}
                                </div>
                              ) : (
                                <div
                                  className={`check-box ${isSelected ? "checked" : ""}`}
                                >
                                  {isSelected && (
                                    <i
                                      className="fa-solid fa-check"
                                      style={{ fontSize: "0.6rem", color: "#fff" }}
                                    ></i>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="vincular-actividad-info">
                              <span className="vincular-actividad-titulo">
                                {actividad.plantilla?.titulo ||
                                  actividad.titulo ||
                                  "Actividad sin título"}
                                {actividad.reportes?.length > 0 && (
                                  <span
                                    className="vincular-reportes-count"
                                    title={`${actividad.reportes.length} reporte(s) vinculado(s)`}
                                  >
                                    <i className="fa-solid fa-file-alt"></i>
                                    {actividad.reportes.length}
                                  </span>
                                )}
                                {isAlreadyLinkedTo(actividad) && (
                                  <span className="vincular-ya-vinculado-tag">
                                    Ya vinculado
                                  </span>
                                )}
                              </span>
                              <div className="vincular-actividad-meta">
                                <span className="vincular-meta-item fecha-item">
                                  <i className="fa-solid fa-calendar"></i>
                                  {formatFechaActividad(actividad.fechainicio)}
                                  {actividad.fechafin &&
                                    ` - ${formatFechaActividad(actividad.fechafin)}`}
                                </span>
                                <span className="vincular-meta-item">
                                  <i className="fa-solid fa-tools"></i>
                                  {actividad.tipoMantenimiento
                                    ?.tipomantenimiento || "Sin tipo"}
                                </span>
                                {actividad.semana && (
                                  <span className="vincular-meta-item">
                                    <i className="fa-solid fa-calendar-week"></i>
                                    Semana {actividad.semana}
                                  </span>
                                )}
                              </div>
                              {actividad.subcomponentes &&
                                actividad.subcomponentes.length > 0 && (
                                  <div className="vincular-actividad-subcomps">
                                    {actividad.subcomponentes.map((sub) => (
                                      <span
                                        key={sub.idsubcomponentesistemaplanta}
                                        className="vincular-subcomp-tag"
                                      >
                                        {sub.descripcion}
                                      </span>
                                    ))}
                                  </div>
                                )}
                            </div>
                            {actividad.plantilla?.color && (
                              <div
                                className="vincular-actividad-color"
                                style={{
                                  backgroundColor: actividad.plantilla.color,
                                }}
                              ></div>
                            )}
                          </div>
                          );
                        })
                      )}
                    </div>

                    <div className="vincular-panel-footer">
                      <button
                        className="vincular-cancel-btn"
                        onClick={() =>
                          setModalState((p) => ({ ...p, showPanel: false, actividades: [], actividadesADesvincular: [] }))
                        }
                      >
                        Cancelar
                      </button>
                      {modalState.actividadesADesvincular.length > 0 && (
                        <button
                          className="desvincular-confirm-btn"
                          onClick={handleDesvincular}
                          disabled={actionState.desvinculando}
                        >
                          {actionState.desvinculando ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin"></i>
                              Desvinculando...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-link-slash"></i>
                              {modalState.actividadesADesvincular.length > 1
                                ? `Desvincular ${modalState.actividadesADesvincular.length} Actividades`
                                : "Desvincular Actividad"}
                            </>
                          )}
                        </button>
                      )}
                      {modalState.actividades.length > 0 && (
                        <button
                          className="vincular-confirm-btn"
                          onClick={handleVincular}
                          disabled={actionState.vinculando}
                        >
                          {actionState.vinculando ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin"></i>
                              Vinculando...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-link"></i>
                              {modalState.actividades.length > 1
                                ? `Vincular ${modalState.actividades.length} Actividades`
                                : "Vincular Reporte"}
                            </>
                          )}
                        </button>
                      )}
                      {!modalState.actividades.length &&
                        !modalState.actividadesADesvincular.length && (
                          <button
                            className="vincular-confirm-btn"
                            disabled
                          >
                            <i className="fa-solid fa-link"></i>
                            Vincular Reporte
                          </button>
                        )}
                    </div>
                  </div>
                )}

                {modalState.mode === "escaneado" ? (
                  <div className="scanned-report-viewer">
                    <div className="scanned-report-info">
                      <p>
                        <i className="fa-solid fa-info-circle"></i>
                        Este reporte fue escaneado desde un documento físico
                      </p>
                    </div>
                    {modalState.scannedUrl ? (
                      <>
                        <ScannedPdfViewer
                          url={modalState.scannedUrl}
                          className="pdf-viewer"
                        />
                        <button
                          className="retry-pdf-btn"
                          onClick={handleRetryScannedUrl}
                          title="Recargar si el PDF no se muestra"
                        >
                          <i className="fa-solid fa-rotate-right"></i> ¿No se
                          muestra? Reintentar
                        </button>
                      </>
                    ) : (
                      <div className="no-pdf">
                        <i className="fa-solid fa-file-pdf"></i>
                        <p>No hay contenido disponible</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="report-sections">
                    {report.respuesta?.secciones ? (
                      report.respuesta.secciones.length > 0 ? (
                        report.respuesta.secciones.map((seccion) =>
                          renderSection(seccion),
                        )
                      ) : (
                        <div className="no-sections">
                          <i className="fa-solid fa-inbox"></i>
                          <p>Este reporte no tiene secciones para mostrar</p>
                        </div>
                      )
                    ) : (
                      <div className="no-sections">
                        <i className="fa-solid fa-exclamation-triangle"></i>
                        <p>No se pudo cargar el contenido del reporte</p>
                        <small>
                          El reporte no tiene la estructura esperada
                        </small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Overlay de generación de PDF */}
      {actionState.generatingPDF && (
        <FullPageLoader
          message="Generando PDF..."
          submessage="Esto puede tardar unos segundos"
          backdrop="blur"
          spinnerSize="xlarge"
          spinnerColor="accent"
        />
      )}
    </>
  );
};

export default ReporteDetalleModal;
